<template>
  <SettingsPageLayout>
    <div v-if="loaded">
      <DetailForm
        v-if="user.postmarkEmailVerified"
        title="Email"
        description="Easily send emails to your candidates and teammates without leaving Humaniz."
        action="Save"
        :hidecancel="true"
        @handle-action="updateUser"
      >
        <div class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
          Verified
        </div>
        <div class="text-sm leading-normal text-gray-500">
          Your email is has been verified and is ready to send email from Humaniz! Make sure to set your signature below
          for consistency and the highest deliverability rates.
        </div>
        <div class="mt-4 bg-gray-50 px-4 py-6 sm:px-6">
          <div class="mt-4 flex space-x-3">
            <div class="min-w-0 flex-1">
              <QuillInput
                v-model="user.emailSignature"
                name="new-signature"
                label="New Signature"
                :label-sr-only="true"
                size="large"
              />
            </div>
          </div>
        </div>
      </DetailForm>
      <DetailForm
        v-else
        title="Email"
        description="Easily send emails to your candidates and teammates without leaving Humaniz."
        action="Verify Email"
        :hidecancel="true"
        @handle-action="verifyEmail"
      >
        <div class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
          Unverified
        </div>
        <div class="text-sm leading-normal text-gray-500">
          Your email is currently unverified for sending Email directly from Humaniz. Please verify your email using the
          link sent to you from Postmark. If you did receive a link, use the Verify Email button below to receive a new
          link from Postmark.
        </div>
      </DetailForm>
    </div>
  </SettingsPageLayout>
</template>

<script>
import { mapState } from 'vuex';
import SettingsPageLayout from '../../SettingsPageLayout/index.vue';
import DetailForm from '../../shared/DetailForm.vue';
import { sanitizeRichText } from '../../../utilities/sanitization';
import { verifyUserEmail } from '../../../api/users';
import QuillInput from '../../shared/QuillInput.vue';

export default {
  components: {
    SettingsPageLayout,
    DetailForm,
    QuillInput,
  },
  data() {
    return {
      loaded: false,
    };
  },
  computed: {
    ...mapState({ user: state => state.general.currentUser }),
  },
  async mounted() {
    await this.getUser();
  },
  methods: {
    sanitize(html) {
      return sanitizeRichText(html);
    },
    async getUser() {
      await this.$store.dispatch('general/loadCurrentUser');
      this.loaded = true;
    },
    async verifyEmail() {
      this.$store.commit('general/showLocalLoadingSpinner');
      const data = await verifyUserEmail();
      this.$store.commit('general/hideLocalLoadingSpinner');
      this.$store.commit('general/showAlert', {
        alertType: 'success',
        alertTitle: 'Email verification sent',
        alertDescription: data.message,
      });

      await this.$store.dispatch('general/loadCurrentUser');
    },
    async updateUser() {
      this.$store.commit('general/showLocalLoadingSpinner');
      await this.$store.dispatch('general/updateUser', {
        user: this.user,
        updatedUserAttributes: {
          email_signature: this.user.emailSignature,
        },
      });
      this.$store.commit('general/hideLocalLoadingSpinner');
      this.$store.commit('general/showAlert', {
        alertType: 'success',
        alertTitle: 'Email settings updated',
        alertDescription: 'Your Email settings were successfully updated.  Use the button below to dismiss this alert.',
      });
    },
  },
};
</script>

<style>
@reference "tailwindcss";
.user-generated-content {
  font-size: 12px;
}
.user-generated-content h1 {
  font-size: 36.62px;
  line-height: 36.62px;
  @apply mt-2 mb-1;
}
.user-generated-content h2 {
  font-size: 29.3px;
  line-height: 29.3px;
  @apply mt-2 mb-1;
}
.user-generated-content h3 {
  font-size: 23.44px;
  line-height: 23.44px;
  @apply mt-2 mb-1;
}
.user-generated-content h4 {
  font-size: 18.75px;
  line-height: 18.75px;
  @apply mt-2 mb-1;
}
.user-generated-content strong {
  font-weight: bold;
}
.user-generated-content em {
  font-style: italic;
}
.user-generated-content a {
  @apply underline text-indigo-500;
}
.user-generated-content ol {
  @apply list-decimal ml-4 mt-2;
}
.user-generated-content ul {
  @apply list-disc ml-4 mt-2;
}
</style>
